import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Lock, User } from 'lucide-react';


export const Login = () => {
    const { login, updateRingState, ringState } = usePIM();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Procedural Stealth Audio Engine (LCARS Inspired)
    const playAudio = (type) => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const masterGain = ctx.createGain();
            masterGain.gain.setValueAtTime(0.2, ctx.currentTime);
            masterGain.connect(ctx.destination);

            if (type === 'collapse') {
                // Sound 2: System Scan (LCARS-style double-pip)
                [0, 0.08].forEach(delay => {
                    const osc = ctx.createOscillator();
                    const g = ctx.createGain();
                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(880, ctx.currentTime + delay);
                    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + delay + 0.05);
                    g.gain.setValueAtTime(0.1, ctx.currentTime + delay);
                    g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.05);
                    osc.connect(g);
                    g.connect(masterGain);
                    osc.start(ctx.currentTime + delay);
                    osc.stop(ctx.currentTime + delay + 0.05);
                });
            } else if (type === 'success') {
                // Sound 3: Access Granted (Star Trek Chime)
                [523.25, 659.25, 783.99].forEach((freq, i) => {
                    const osc = ctx.createOscillator();
                    const g = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.06);
                    osc.frequency.exponentialRampToValueAtTime(freq * 1.05, ctx.currentTime + i * 0.06 + 0.4);
                    g.gain.setValueAtTime(0, ctx.currentTime + i * 0.06);
                    g.gain.linearRampToValueAtTime(0.15, ctx.currentTime + i * 0.06 + 0.1);
                    g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.06 + 0.5);
                    osc.connect(g);
                    g.connect(masterGain);
                    osc.start(ctx.currentTime + i * 0.06);
                    osc.stop(ctx.currentTime + i * 0.06 + 0.5);
                });
            } else if (type === 'failure') {
                // Sound 4: Access Denied (Classic LCARS Double-Beep)
                [0, 0.15].forEach(delay => {
                    const osc = ctx.createOscillator();
                    const g = ctx.createGain();
                    osc.type = 'square';
                    osc.frequency.setValueAtTime(110, ctx.currentTime + delay);
                    g.gain.setValueAtTime(0.2, ctx.currentTime + delay);
                    g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.12);
                    osc.connect(g);
                    g.connect(masterGain);
                    osc.start(ctx.currentTime + delay);
                    osc.stop(ctx.currentTime + delay + 0.12);
                });
            }
        } catch (e) { console.warn("Audio Context failed", e); }
    };

    // Calculate dynamic background color for the card
    const getCardBackground = () => {
        if (!ringState || ringState.isSuccess || ringState.flareIntensity <= 0) {
            return 'rgba(10, 10, 12, 0.2)';
        }

        const pulse = (Math.sin(Date.now() / 400) + 1) / 2;
        const effectiveIntensity = Math.pow(ringState.flareIntensity, 1.5) * (0.7 + 0.3 * pulse);

        const r = Math.round(10 + (ringState.flareColor[0] - 10) * effectiveIntensity);
        const g = Math.round(10 + (ringState.flareColor[1] - 10) * effectiveIntensity);
        const b = Math.round(12 + (ringState.flareColor[2] - 12) * effectiveIntensity);

        return `rgba(${r}, ${g}, ${b}, 0.2)`;
    };

    const triggerPulse = (isOk) => {
        // 1. Collapse
        updateRingState({ targetRadiusMultiplier: 0.05 });
        playAudio('collapse');

        // 2. Expand + Flare
        setTimeout(() => {
            if (isOk) {
                setIsSuccess(true);
                playAudio('success');
                updateRingState({
                    isSuccess: true,
                    flareColor: [20, 220, 20],
                    targetRadiusMultiplier: 1.2,
                    flareIntensity: 1.0,
                    flareTriggerTime: Date.now()
                });
            } else {
                playAudio('failure');
                updateRingState({
                    targetRadiusMultiplier: 1.0,
                    flareColor: [220, 20, 20],
                    flareIntensity: 1.0,
                    flareTriggerTime: Date.now()
                });
            }
        }, 300);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Initial Collapse (Haptics)
        updateRingState({ targetRadiusMultiplier: 0.05 });
        playAudio('collapse');

        setTimeout(async () => {
            try {
                await login(username, password);
                // SUCCESS
                setIsSuccess(true);
                playAudio('success');
                updateRingState({
                    isSuccess: true,
                    flareColor: [20, 220, 20],
                    targetRadiusMultiplier: 1.2,
                    flareIntensity: 1.0,
                    flareTriggerTime: Date.now()
                });
            } catch (err) {
                // FAILURE (Stealth mode: No text error message)
                playAudio('failure');
                updateRingState({
                    targetRadiusMultiplier: 1.0,
                    flareColor: [220, 20, 20],
                    flareIntensity: 1.0,
                    flareTriggerTime: Date.now()
                });
            } finally {
                setLoading(false);
            }
        }, 300);
    };

    // Transition logic is handled centrally in App.jsx and ring physics are in PIMContext.jsx
    // We only trigger updates here.

    return (
        <div className="h-screen w-full bg-transparent flex items-center justify-center relative overflow-hidden font-sans selection:bg-blue-500/30">
            <style>{`
                @keyframes move-1 {
                    0% { transform: translate(0, 0) scale(1); }
                    33% { transform: translate(50px, -30px) scale(1.1); }
                    66% { transform: translate(-20px, 40px) scale(0.95); }
                    100% { transform: translate(0, 0) scale(1); }
                }
                @keyframes move-2 {
                    0% { transform: translate(0, 0) rotate(0deg) scale(1); }
                    50% { transform: translate(-30px, 40px) rotate(5deg) scale(1.05); }
                    100% { transform: translate(0, 0) rotate(0deg) scale(1); }
                }
                @keyframes move-3 {
                    0% { transform: translate(0, 0) scale(1); }
                    50% { transform: translate(40px, 40px) scale(0.9); }
                    100% { transform: translate(0, 0) scale(1); }
                }
                @keyframes pulse-glow {
                    0%, 100% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
                    50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
                }
                .nebula-blob {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(80px); /* Reduced slightly for better performance */
                    mix-blend-mode: screen;
                    will-change: transform; /* Hint for browser optimization */
                }
                .glass-card {
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 0.3px solid rgba(255, 255, 255, 0.1);
                }
                .input-field {
                    background: rgba(20, 20, 25, 0.6) !important; /* Consistent target grey */
                    border: 1px solid rgba(255, 255, 255, 0.1) !important;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    color: #E0E0E0;
                    outline: none !important;
                    box-shadow: none !important;
                    -webkit-appearance: none;
                    appearance: none;
                    font-size: 14px !important;
                    font-weight: 300 !important;
                }
                /* Autofill Overrides for Stealth Look */
                .input-field:-webkit-autofill,
                .input-field:-webkit-autofill:hover,
                .input-field:-webkit-autofill:focus {
                    -webkit-text-fill-color: #E0E0E0 !important;
                    -webkit-box-shadow: 0 0 0px 1000px rgba(20, 20, 25, 1) inset !important;
                    transition: background-color 5000s ease-in-out 0s;
                }
                .input-field:focus {
                    background: rgba(20, 20, 25, 0.6) !important; /* Keep background same */
                    border-color: rgba(255, 255, 255, 0.5) !important; /* Brighten border only */
                    outline: none !important;
                }
                 .btn-glow {
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                }
                .btn-glow::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
                    transition: left 0.5s ease;
                }
                .btn-glow:hover::before {
                    left: 100%;
                }
                .btn-glow:hover {
                    box-shadow: 0 0 20px rgba(255,255,255,0.1);
                    background: rgba(255, 255, 255, 0.05);
                }
                .emp-blackout {
                    opacity: 0.1 !important;
                    transition: none !important;
                    backdrop-filter: blur(0px) !important;
                    -webkit-backdrop-filter: blur(0px) !important;
                    border-color: transparent !important;
                    box-shadow: none !important;
                }
            `}</style>

            {/* Particle Ring is now global in App.jsx */}

            {/* Subtle Glow Background is now handled globally in App.jsx */}



            <div
                id="login-card"
                className={`glass-card w-[260px] p-6 rounded-[2rem] flex flex-col space-y-5 relative z-10 transition-all duration-1000 ${isSuccess ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100'}`}
                style={{
                    background: getCardBackground()
                }}
            >
                <div className="text-center">
                    <p className="text-[16px] text-[#E0E0E0] uppercase tracking-[0.4em] font-manrope opacity-90">PIM System</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                        type="text"
                        className="input-field w-full px-4 py-2 rounded-full"
                        placeholder="ID-TOKEN"
                        autocomplete="off"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                    <input
                        type="password"
                        className="input-field w-full px-4 py-2 rounded-full"
                        placeholder="ACCESS-KEY"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />

                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-glow w-full py-2 px-4 bg-white/[0.02] border border-white/10 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase text-[#E0E0E0] hover:text-white transition-colors mt-2"
                    >
                        {loading ? 'Validating...' : 'Enter'}
                    </button>
                </form>

                <div className="text-center text-[8px] text-white/10 tracking-[0.2em] uppercase">
                    &copy; 2026 PIM System
                </div>
            </div>
        </div>
    );
};

